import { useEffect, useRef } from 'react'

import { deleteFileV3 } from '../../../../../api'
import { updateStateValue } from '../../../../../utils/react'
import { getCheckedFilesListByChunk, useFileUpload } from '../../../../File'
import {
  getPhotoFileInstance,
  getPhotoTileId,
  getPhotoUploadResult,
  getPhotoUploadResultTileIds,
  toPhotoRetryFile,
  toPhotoUploadTiles,
} from '../../RentalPortalPhotoGallery.utils'

import {
  getPhotoCheckedFilesList,
  getPhotoUploadTile,
} from './useRentalPortalPhotoGalleryUpload.utils'

export function useRentalPortalPhotoGalleryUpload({
  photos,
  onChange,
  onUploadingFilesChange,
}) {
  const uploadTileIndexRef = useRef(0)

  const tileIdByFileRef = useRef(new WeakMap())

  const tileIdByResultRef = useRef(new WeakMap())

  const cancelledTileIdsRef = useRef(new Set())

  const photosRef = useRef(photos)

  async function handleDeletePhotoFile(fileId) {
    try {
      await deleteFileV3(fileId)
    } catch (error) {
      console.error(error)
    }
  }

  function setPhotos(updater) {
    photosRef.current = updateStateValue(updater, photosRef.current)

    onChange(photosRef.current)
  }

  const {
    filesError,
    filesUploading,
    handleClearFilesUploading,
    handleExcludeFile,
    handleRemoveFile,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
  } = useFileUpload({
    files: photos,
    onDeleteFile: handleDeletePhotoFile,
    onSetFiles: setPhotos,
  })

  useEffect(() => {
    photosRef.current = photos
  }, [photos])

  useEffect(() => {
    onUploadingFilesChange?.(filesUploading)
  }, [filesUploading])

  function resolveUploadedPhotoTile(file) {
    return getPhotoUploadTile({
      cancelledTileIds: cancelledTileIdsRef.current,
      file,
      tileIdByFile: tileIdByFileRef.current,
      tileIdByResult: tileIdByResultRef.current,
    })
  }

  function handleStartUpload(files) {
    handleStartUploading(
      toPhotoUploadTiles({
        files,
        startIndex: uploadTileIndexRef.current,
        tileIdByFile: tileIdByFileRef.current,
      })
    )

    uploadTileIndexRef.current += files.length
  }

  function handleFinishUpload(acceptedFiles) {
    const keptFiles = []

    acceptedFiles.forEach(file => {
      const { isCancelled, tileId } = resolveUploadedPhotoTile(file)

      if (tileId) {
        handleRemoveFile(tileId)
      } else {
        handleClearFilesUploading()
      }

      if (isCancelled) {
        handleDeletePhotoFile(file.id)

        return
      }

      keptFiles.push(file)
    })

    if (keptFiles.length > 0) {
      handleUploadFile(keptFiles)
    }
  }

  function handleErrorUpload(errors) {
    const errorFiles = []

    errors.forEach(file => {
      const { isCancelled, tileId } = resolveUploadedPhotoTile(file)

      if (isCancelled) {
        return
      }

      if (tileId) {
        errorFiles.push({ ...file, id: tileId })

        return
      }

      if (getPhotoFileInstance(file)) {
        handleClearFilesUploading()
      }

      errorFiles.push({
        ...file,
        id: getPhotoTileId(file.name, uploadTileIndexRef.current),
      })

      uploadTileIndexRef.current += 1
    })

    if (errorFiles.length > 0) {
      handleUploadErrors(errorFiles)
    }
  }

  async function handleGetUploadedFiles(params) {
    const results = await getCheckedFilesListByChunk({
      ...params,
      getFilesList: getPhotoCheckedFilesList,
    })

    getPhotoUploadResultTileIds({
      files: params.files,
      results,
      tileIdByFile: tileIdByFileRef.current,
    }).forEach(([result, tileId]) => {
      tileIdByResultRef.current.set(result, tileId)
    })

    return results
  }

  function handleRemoveUploadTile(tileId) {
    if (filesUploading.some(file => file.id === tileId)) {
      cancelledTileIdsRef.current.add(tileId)
    }

    handleRemoveFile(tileId)
  }

  async function handleRetryPhoto(photo) {
    handleExcludeFile(photo.id)

    handleStartUploading([toPhotoRetryFile(photo)])

    const { acceptedFiles, rejectedFiles } = getPhotoUploadResult(
      await handleGetUploadedFiles({ files: [photo] })
    )

    handleFinishUpload(acceptedFiles)

    handleErrorUpload(rejectedFiles)
  }

  return {
    filesError,
    filesUploading,
    handleErrorUpload,
    handleExcludeFile,
    handleFinishUpload,
    handleGetUploadedFiles,
    handleRemoveUploadTile,
    handleRetryPhoto,
    handleStartUpload,
    setPhotos,
  }
}
